"use client";

import { cn } from "@/lib/utils";
import type { TextareaHTMLAttributes } from "react";
import { useEffect, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  name: string;
  label?: string;
  classNameContainer?: string;
  maxLength?: number;
  noBorder?: boolean;
  noShadow?: boolean;
};

export function TextArea({
  name,
  label,
  placeholder,
  className,
  classNameContainer,
  rows = 10,
  maxLength,
  required,
  noBorder = false,
  noShadow = false,
  ...textareaProps
}: TextAreaProps) {
  const { control } = useFormContext();

  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  const [currentLength, setCurrentLength] = useState(
    field.value ? String(field.value).length : 0
  );

  useEffect(() => {
    setCurrentLength(field.value ? String(field.value).length : 0);
  }, [field.value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value;
    
    // Enforce character limit strictly - truncate if necessary
    if (maxLength && value.length > maxLength) {
      value = value.substring(0, maxLength);
      // Update the textarea value to show the truncated text
      e.target.value = value;
    }
    
    // Create a new event with the potentially modified value
    const modifiedEvent = {
      ...e,
      target: { ...e.target, value },
    };
    
    field.onChange(modifiedEvent);
    setCurrentLength(value.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Allow navigation keys, backspace, delete, etc. even at limit
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 
      'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'
    ];
    
    if (maxLength && currentLength >= maxLength && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
    
    // Call original onKeyDown if provided
    if (textareaProps.onKeyDown) {
      textareaProps.onKeyDown(e);
    }
  };

  const isNearLimit = maxLength && currentLength >= maxLength * 0.9;
  const isAtLimit = maxLength && currentLength >= maxLength;

  return (
    <div className={cn("mt-3", classNameContainer)}>
      {label && (
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm text-gray-600 font-semibold">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          {maxLength && (
            <span className={cn(
              "text-xs font-medium",
              isAtLimit ? "text-red-500" : 
              isNearLimit ? "text-yellow-600" : 
              "text-gray-500"
            )}>
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      )}

      <div className="relative">
        <textarea
          {...field}
          {...textareaProps}
          placeholder={placeholder}
          rows={rows}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={cn(
            "block w-full px-4 py-2 mt-2 text-sm text-gray-700 placeholder-gray-400 rounded-md resize-none",
            !noShadow && "shadow-sm",
            !noBorder && "border",
            error && !noBorder ? "border-red-500" : "",
            !error && !noBorder ? "border-gray-200" : "",
            isAtLimit ? "border-red-500" : "focus:border-brand-400",
            "focus:ring-brand-400 focus:outline-none focus:ring focus:ring-opacity-40",
            className?.includes('bg-') ? "" : "bg-white",
            className
          )}
        />
      </div>

      {isAtLimit && (
        <span className="text-red-500 text-xs mt-1 block">
          Character limit reached. Cannot add more text.
        </span>
      )}

      {error && (
        <span className="text-red-500 text-xs mt-1 block">{error.message}</span>
      )}
    </div>
  );
}
